import React, { useRef, useState } from 'react';
import Button from '../../../../../shared/components/Button/Button';
import IconButton from '../../../../../shared/components/IconButton/IconButton';
import Menu, { MenuGroup, MenuItem } from '../../../../../shared/components/Menu/Menu';
import {
  ChecklistIcon,
  CodeIcon,
  FormatAlignCenterIcon,
  FormatAlignJustifyIcon,
  FormatAlignLeftIcon,
  FormatAlignRightIcon,
  FormatBoldIcon,
  FormatClearIcon,
  FormatIndentDecreaseIcon,
  FormatIndentIncreaseIcon,
  FormatItalicIcon,
  FormatListBulletedIcon,
  FormatListNumberedIcon,
  FormatQuoteIcon,
  FormatUnderlinedIcon,
  HighlightIcon,
  HorizontalRuleIcon,
  ImageIcon,
  LinkIcon,
  RedoIcon,
  StrikethroughSIcon,
  TableChartIcon,
  TitleIcon,
  UndoIcon,
} from '../WikiIcons';
import WikiImageDialog from './WikiImageDialog';
import WikiLinkDialog from './WikiLinkDialog';
import WikiTableMenu from './WikiTableMenu';

const buttonClass = (active) => (active ? 'wiki-toolbar-button active' : 'wiki-toolbar-button');

function ToolButton({ active, disabled, label, onClick, children }) {
  return (
    <IconButton
      active={active}
      ariaLabel={label}
      className={buttonClass(active)}
      disabled={disabled}
      size="small"
      tooltip={label}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
}

function WikiEditorToolbar({ editor }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [listsOpen, setListsOpen] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const styleButtonRef = useRef(null);
  const listsButtonRef = useRef(null);
  const insertButtonRef = useRef(null);
  const moreButtonRef = useRef(null);
  const tableButtonRef = useRef(null);

  if (!editor) {
    return null;
  }

  const currentLinkAttributes = editor.getAttributes('link') || {};
  const currentLink = currentLinkAttributes.href || '';
  const selectedText = editor.state.doc.textBetween(
    editor.state.selection.from,
    editor.state.selection.to,
    ' '
  );
  const canToggleBold = editor.can().chain().toggleBold().run();
  const canToggleItalic = editor.can().chain().toggleItalic().run();
  const canIndent = editor.can().sinkListItem('listItem') || editor.can().sinkListItem('taskItem');
  const canOutdent = editor.can().liftListItem('listItem') || editor.can().liftListItem('taskItem');
  const indentList = () => (
    editor.chain().focus().sinkListItem('listItem').run()
    || editor.chain().focus().sinkListItem('taskItem').run()
  );
  const outdentList = () => (
    editor.chain().focus().liftListItem('listItem').run()
    || editor.chain().focus().liftListItem('taskItem').run()
  );
  const closeMenus = () => {
    setStyleOpen(false);
    setListsOpen(false);
    setInsertOpen(false);
    setMoreOpen(false);
  };
  const runCommand = (command) => {
    command();
    closeMenus();
  };

  return (
    <div className="wiki-tiptap-toolbar" role="toolbar" aria-label="Outils de rédaction Wiki">
      <div className="wiki-toolbar-group">
        <span ref={styleButtonRef}>
          <Button
            className="wiki-toolbar-menu-button"
            iconStart={<TitleIcon fontSize="small" />}
            size="small"
            variant={styleOpen ? 'secondary' : 'ghost'}
            onClick={() => setStyleOpen((current) => !current)}
          >
            Style
          </Button>
        </span>
      </div>
      <div className="wiki-toolbar-group">
        <ToolButton label="Gras Ctrl+B" active={editor.isActive('bold')} disabled={!canToggleBold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <FormatBoldIcon fontSize="small" />
        </ToolButton>
        <ToolButton label="Italique Ctrl+I" active={editor.isActive('italic')} disabled={!canToggleItalic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <FormatItalicIcon fontSize="small" />
        </ToolButton>
        <ToolButton label="Souligner Ctrl+U" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <FormatUnderlinedIcon fontSize="small" />
        </ToolButton>
      </div>
      <div className="wiki-toolbar-group">
        <span ref={listsButtonRef}>
          <Button
            className="wiki-toolbar-menu-button"
            iconStart={<FormatListBulletedIcon fontSize="small" />}
            size="small"
            variant={listsOpen ? 'secondary' : 'ghost'}
            onClick={() => setListsOpen((current) => !current)}
          >
            Listes
          </Button>
        </span>
      </div>
      <div className="wiki-toolbar-group">
        <ToolButton label="Lien" active={editor.isActive('link')} onClick={() => setLinkOpen(true)}>
          <LinkIcon fontSize="small" />
        </ToolButton>
        <span ref={insertButtonRef}>
          <Button
            className="wiki-toolbar-menu-button"
            iconStart={<ImageIcon fontSize="small" />}
            size="small"
            variant={insertOpen ? 'secondary' : 'ghost'}
            onClick={() => setInsertOpen((current) => !current)}
          >
            Inserer
          </Button>
        </span>
        <span ref={moreButtonRef}>
          <Button
            className="wiki-toolbar-menu-button"
            iconStart={<FormatClearIcon fontSize="small" />}
            size="small"
            variant={moreOpen ? 'secondary' : 'ghost'}
            onClick={() => setMoreOpen((current) => !current)}
          >
            Plus
          </Button>
        </span>
      </div>
      <div className="wiki-toolbar-spacer" />
      <div className="wiki-toolbar-group">
        <ToolButton label="Annuler Ctrl+Z" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <UndoIcon fontSize="small" />
        </ToolButton>
        <ToolButton label="Retablir Ctrl+Shift+Z" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <RedoIcon fontSize="small" />
        </ToolButton>
      </div>

      <Menu anchorRef={styleButtonRef} open={styleOpen} onClose={() => setStyleOpen(false)} className="wiki-toolbar-menu">
        <MenuGroup label="Style">
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setParagraph().run())}>
            Paragraphe
          </MenuItem>
          {[1, 2, 3].map((level) => (
            <MenuItem key={level} onClick={() => runCommand(() => editor.chain().focus().toggleHeading({ level }).run())}>
              Titre {level}
            </MenuItem>
          ))}
        </MenuGroup>
      </Menu>

      <Menu anchorRef={listsButtonRef} open={listsOpen} onClose={() => setListsOpen(false)} className="wiki-toolbar-menu">
        <MenuGroup label="Listes">
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleBulletList().run())}>
            <FormatListBulletedIcon fontSize="small" /> Liste à puces
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleOrderedList().run())}>
            <FormatListNumberedIcon fontSize="small" /> Liste numerotée
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleTaskList().run())}>
            <ChecklistIcon fontSize="small" /> Liste de tâches
          </MenuItem>
          <MenuItem disabled={!canOutdent} onClick={() => runCommand(outdentList)}>
            <FormatIndentDecreaseIcon fontSize="small" /> Désindenter
          </MenuItem>
          <MenuItem disabled={!canIndent} onClick={() => runCommand(indentList)}>
            <FormatIndentIncreaseIcon fontSize="small" /> Indenter
          </MenuItem>
        </MenuGroup>
      </Menu>

      <Menu anchorRef={insertButtonRef} open={insertOpen} onClose={() => setInsertOpen(false)} className="wiki-toolbar-menu">
        <MenuGroup label="Insérer">
          <MenuItem onClick={() => {
            setImageOpen(true);
            closeMenus();
          }}>
            <ImageIcon fontSize="small" /> Image
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleBlockquote().run())}>
            <FormatQuoteIcon fontSize="small" /> Citation
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setHorizontalRule().run())}>
            <HorizontalRuleIcon fontSize="small" /> Séparateur
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleCodeBlock().run())}>
            <CodeIcon fontSize="small" /> Bloc de code
          </MenuItem>
          <MenuItem onClick={() => {
            setTableOpen(true);
            closeMenus();
          }}>
            <TableChartIcon fontSize="small" /> Tableau
          </MenuItem>
        </MenuGroup>
      </Menu>

      <Menu anchorRef={moreButtonRef} open={moreOpen} onClose={() => setMoreOpen(false)} className="wiki-toolbar-menu">
        <MenuGroup label="Mise en forme">
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleStrike().run())}>
            <StrikethroughSIcon fontSize="small" /> Barre
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleHighlight().run())}>
            <HighlightIcon fontSize="small" /> Surlignage
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().toggleCode().run())}>
            <CodeIcon fontSize="small" /> Code inline
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().unsetAllMarks().clearNodes().run())}>
            <FormatClearIcon fontSize="small" /> Supprimer la mise en forme
          </MenuItem>
        </MenuGroup>
        <MenuGroup label="Alignement">
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setTextAlign('left').run())}>
            <FormatAlignLeftIcon fontSize="small" /> Gauche
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setTextAlign('center').run())}>
            <FormatAlignCenterIcon fontSize="small" /> Centre
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setTextAlign('right').run())}>
            <FormatAlignRightIcon fontSize="small" /> Droite
          </MenuItem>
          <MenuItem onClick={() => runCommand(() => editor.chain().focus().setTextAlign('justify').run())}>
            <FormatAlignJustifyIcon fontSize="small" /> Justifier
          </MenuItem>
        </MenuGroup>
      </Menu>

      <span ref={tableButtonRef} className="wiki-table-menu-anchor" />
      <WikiTableMenu anchorRef={tableButtonRef} open={tableOpen} editor={editor} onClose={() => setTableOpen(false)} />

      <WikiLinkDialog
        initialNewTab={currentLinkAttributes.target !== null}
        initialText={selectedText}
        initialUrl={currentLink}
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onRemove={() => {
          editor.chain().focus().unsetLink().run();
          setLinkOpen(false);
        }}
        onSubmit={({ href, target, text }) => {
          const linkAttributes = target
            ? { href, target, rel: 'noreferrer' }
            : { href };

          if (text && editor.state.selection.empty) {
            editor.chain().focus().insertContent({
              type: 'text',
              text,
              marks: [{ type: 'link', attrs: linkAttributes }],
            }).run();
          } else {
            editor.chain().focus().extendMarkRange('link').setLink(linkAttributes).run();
          }
          setLinkOpen(false);
        }}
      />
      <WikiImageDialog
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        onSubmit={(attrs) => {
          editor.chain().focus().setImage(attrs).run();
          setImageOpen(false);
        }}
      />
    </div>
  );
}

export default WikiEditorToolbar;
